import React, { useEffect, useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { ChatPreview } from "../components/ChatPreview";
import { SliderInput } from "../components/SliderInput";
import { agentKeys } from "../queryKeys";

const WIDGET_ICON_OPTIONS: { id: string; label: string }[] = [
  { id: "chat", label: "Chat" },
  { id: "support_agent", label: "Support" },
  { id: "smart_toy", label: "Robot" },
  { id: "psychology", label: "Psychology" },
  { id: "question_answer", label: "Q&A" },
  { id: "forum", label: "Forum" },
  { id: "contact_support", label: "Contact" },
  { id: "help", label: "Help" },
  { id: "assistant", label: "Assistant" },
  { id: "chat_bubble", label: "Bubble" },
];

const DOMAIN_REGEX = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/;

function normalizeDomain(value: string): string {
  if (!value || typeof value !== "string") return "";
  let s = value.trim().toLowerCase();
  if (s.includes("://")) s = s.split("://", 1)[1] ?? s;
  s = s.split("/")[0].split("?")[0].trim();
  return s;
}

function isValidDomain(domain: string): boolean {
  if (!domain) return true;
  return DOMAIN_REGEX.test(domain);
}

interface AgentData {
  id: string;
  name: string;
  display_name: string;
  welcome_message: string;
  accent_color: string;
  persona: string;
  domain: string;
  llm_model: string;
  system_instructions: string;
  temperature: number;
  max_tokens: number;
  status: string;
  theme: string;
  enabled: boolean;
  icon?: string;
}

interface AgentSettingsPageProps {
  agentId: string;
  onSave?: () => Promise<void>;
}

export function AgentSettingsPage({ agentId, onSave }: AgentSettingsPageProps) {
  const queryClient = useQueryClient();
  const { data: serverAgent, isLoading: loading } = useQuery({
    queryKey: agentKeys.detail(agentId),
    queryFn: () => apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`).then((r) => r.agent),
  });
  const [agent, setAgent] = useState<AgentData | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setAgent(serverAgent ?? null);
  }, [serverAgent]);

  const updateField = useCallback((field: keyof AgentData, value: string | number | boolean) => {
    if (!agent) return;
    setAgent({ ...agent, [field]: value });
  }, [agent]);

  const saveMutation = useMutation({
    mutationFn: (payload: AgentData) =>
      apiFetch<{ agent: AgentData }>(`/api/agents/${agentId}`, {
        method: "PUT",
        body: JSON.stringify({
          display_name: payload.display_name,
          domain: payload.domain,
          llm_model: payload.llm_model,
          system_instructions: payload.system_instructions,
          temperature: payload.temperature,
          max_tokens: payload.max_tokens,
          name: payload.name,
          enabled: payload.enabled,
          icon: payload.icon || "chat",
        }),
      }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(agentId) });
      if (onSave) await onSave();
    },
    onError: (err: Error) => setError(err.message || "Save failed"),
  });

  const handleSave = useCallback(async () => {
    if (!agent) return;
    setError("");
    saveMutation.mutate(agent);
  }, [agent, saveMutation]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__agentSaveHandler = handleSave;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete (window as any).__agentSaveHandler;
      }
    };
  }, [handleSave]);

  if (loading)
    return (
      <div className="general-settings">
        <div className="general-settings-form-panel">
          <p style={{ color: "var(--text-muted)", padding: "4rem", textAlign: "center" }}>Loading...</p>
        </div>
        <div className="general-settings-preview-panel chat-preview-dark" />
      </div>
    );
  if (!agent)
    return (
      <div className="general-settings">
        <div className="general-settings-form-panel">
          <p style={{ color: "var(--text-muted)", padding: "4rem", textAlign: "center" }}>{error || "Agent not found"}</p>
        </div>
        <div className="general-settings-preview-panel chat-preview-dark" />
      </div>
    );

  const displayName = agent.display_name || agent.name;
  const modelOptions = [
    { value: "gpt-4o", label: "GPT-4o (Recommended)" },
    { value: "gpt-4o-mini", label: "GPT-4o-mini" },
    { value: "gpt-4-turbo", label: "GPT-4 Turbo" },
    { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
  ];

  return (
    <div className="general-settings">
      <div className="general-settings-form-panel">
        <div className="general-settings-form-inner">
          <div className="general-settings-page-header">
            <h1>General Settings</h1>
            <p className="page-subtitle">Define the core identity and logic of your AI agent.</p>
          </div>

          <div className="general-settings-form">
            <section className="settings-section">
              <h4 className="section-header">
                <span className="material-symbols-outlined">badge</span>
                Agent Identity
              </h4>
              <div className="identity-fields">
                <div>
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={agent.display_name}
                    onChange={(e) => updateField("display_name", e.target.value)}
                    placeholder={agent.name || "e.g. Support Assistant"}
                  />
                </div>
                <div>
                  <label>Domain</label>
                  <input
                    type="text"
                    value={agent.domain || ""}
                    onChange={(e) => updateField("domain", e.target.value)}
                    onBlur={(e) => {
                      const cleaned = normalizeDomain(e.target.value);
                      if (cleaned !== (agent.domain || "")) updateField("domain", cleaned);
                    }}
                    placeholder="e.g. example.com or www.example.com"
                  />
                  <p className="form-tip">
                    Restrict widget access to a specific domain (optional). Use a clean hostname like example.com or www.example.com (no protocol or path).
                  </p>
                  {agent.domain && !isValidDomain(agent.domain) && (
                    <p className="form-tip" style={{ color: "var(--danger)", marginTop: "0.25rem" }}>
                      Invalid domain. Use only hostname, e.g. example.com or www.example.com
                    </p>
                  )}
                </div>
                <div>
                  <label>Widget icon</label>
                  <p className="form-tip" style={{ marginBottom: "0.5rem" }}>
                    Icon shown on the widget button and as the agent avatar in chat.
                  </p>
                  <div className="icon-picker">
                    {WIDGET_ICON_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`icon-picker-btn ${(agent.icon || "chat") === opt.id ? "active" : ""}`}
                        onClick={() => updateField("icon", opt.id)}
                        title={opt.label}
                      >
                        <span className="material-symbols-outlined">{opt.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label>Agent Status</label>
                  <div className="toggle-switch-container">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={agent.enabled !== false}
                        onChange={(e) => updateField("enabled", e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {agent.enabled !== false ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <p className="form-tip">
                    {agent.enabled !== false 
                      ? "Widget is active and visible on your website." 
                      : "Widget is disabled and will not appear on your website."}
                  </p>
                </div>
                <div>
                  <label>LLM Model</label>
                  <select
                    value={agent.llm_model || "gpt-4o"}
                    onChange={(e) => updateField("llm_model", e.target.value)}
                  >
                    {modelOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="settings-section">
              <h4 className="section-header">
                <span className="material-symbols-outlined">description</span>
                System Instructions
              </h4>
              <div>
                <label>Role Definition & Instructions</label>
                <textarea
                  value={agent.system_instructions}
                  onChange={(e) => updateField("system_instructions", e.target.value)}
                  placeholder="Tell the agent who it is and how it should behave..."
                  rows={10}
                  className="system-instructions-textarea"
                />
                <p className="form-tip">
                  Tip: Use variables like {"{user_name}"} to personalize responses.
                </p>
              </div>
            </section>

            <section className="settings-section" style={{ paddingBottom: "2.5rem" }}>
              <h4 className="section-header">
                <span className="material-symbols-outlined">tune</span>
                Parameters
              </h4>
              <div className="parameters-grid">
                <SliderInput
                  label="Temperature"
                  value={Math.max(agent.temperature ?? 0.7, 0.1)}
                  min={0.1}
                  max={1}
                  step={0.1}
                  onChange={(value) => updateField("temperature", value)}
                  helperText="Controls randomness: 0.1 is focused, 1 is creative."
                />
                <SliderInput
                  label="Max Tokens"
                  value={agent.max_tokens || 2048}
                  min={256}
                  max={4096}
                  step={128}
                  onChange={(value) => updateField("max_tokens", value)}
                  helperText="Maximum length of the response."
                />
              </div>
            </section>
          </div>

          {(error || saveMutation.error) && (
            <div className="auth-error">{error || (saveMutation.error as Error)?.message}</div>
          )}
        </div>
      </div>

      <div className={`general-settings-preview-panel ${agent.theme === "light" ? "chat-preview-light" : "chat-preview-dark"}`}>
        <ChatPreview
          agentId={agentId}
          agentName={displayName}
          model={agent.llm_model || "gpt-4o"}
          accentColor={agent.accent_color || "#137fec"}
          theme={agent.theme || "dark"}
          icon={agent.icon ?? "chat"}
        />
      </div>
    </div>
  );
}
